/**
 * Design tokens mirrored from /DESIGN.md ("The Precision Chronometer").
 * DESIGN.md is the source of truth — update both together.
 */

export const colors = {
  light: {
    chronometerGold: 'oklch(0.74 0.16 90)',
    chronometerGoldDeep: 'oklch(0.64 0.15 90)',
    chronometerGoldSubtle: 'oklch(0.94 0.04 90)',
    slateTeal: 'oklch(0.38 0.07 215)',
    slateTealDeep: 'oklch(0.30 0.07 215)',
    bg: 'oklch(1.000 0.000 0)',
    canvas: 'oklch(0.979 0.003 90)',
    surface: 'oklch(0.97 0.006 90)',
    ink: 'oklch(0.20 0.01 90)',
    muted: 'oklch(0.48 0.01 90)',
    border: 'oklch(0.89 0.006 90)',
    success: 'oklch(0.62 0.15 145)',
    warning: 'oklch(0.75 0.16 55)',
    danger: 'oklch(0.55 0.20 25)',
    info: 'oklch(0.55 0.13 240)',
  },
  dark: {
    chronometerGold: 'oklch(0.74 0.16 90)',
    chronometerGoldDeep: 'oklch(0.80 0.15 90)',
    chronometerGoldSubtle: 'oklch(0.30 0.05 90)',
    slateTeal: 'oklch(0.55 0.08 215)',
    slateTealDeep: 'oklch(0.65 0.08 215)',
    bg: 'oklch(0.16 0.005 90)',
    canvas: 'oklch(0.135 0.004 90)',
    surface: 'oklch(0.22 0.006 90)',
    ink: 'oklch(0.96 0.005 90)',
    muted: 'oklch(0.68 0.01 90)',
    border: 'oklch(0.32 0.006 90)',
    success: 'oklch(0.70 0.15 145)',
    warning: 'oklch(0.78 0.16 55)',
    danger: 'oklch(0.65 0.20 25)',
    info: 'oklch(0.68 0.13 240)',
  },
} as const;

/**
 * Alternate accent presets — same tonal ramp (main/deep/subtle at the same
 * lightness/chroma pattern as `colors.light.chronometerGold`/`colors.dark.*`)
 * rotated to a different OKLCH hue. Each preset still honors DESIGN.md's
 * one-accent rule (only one accent hue active at a time, applied via a
 * `.theme-<name>` class on the document root — see theme.css/theme.ts) —
 * this adds *choice of which* accent, not a second simultaneous accent.
 */
export const accentPresets = {
  gold: {
    light: { main: colors.light.chronometerGold, deep: colors.light.chronometerGoldDeep, subtle: colors.light.chronometerGoldSubtle },
    dark: { main: colors.dark.chronometerGold, deep: colors.dark.chronometerGoldDeep, subtle: colors.dark.chronometerGoldSubtle },
  },
  violet: {
    light: { main: 'oklch(0.62 0.19 300)', deep: 'oklch(0.52 0.18 300)', subtle: 'oklch(0.94 0.04 300)' },
    dark: { main: 'oklch(0.72 0.17 300)', deep: 'oklch(0.80 0.15 300)', subtle: 'oklch(0.30 0.06 300)' },
  },
  rose: {
    light: { main: 'oklch(0.64 0.19 350)', deep: 'oklch(0.54 0.18 350)', subtle: 'oklch(0.94 0.04 350)' },
    dark: { main: 'oklch(0.72 0.17 350)', deep: 'oklch(0.80 0.15 350)', subtle: 'oklch(0.30 0.06 350)' },
  },
} as const;

export type AccentPreset = keyof typeof accentPresets;

export const typography = {
  fontFamily: {
    sans: "Inter, -apple-system, 'Segoe UI', sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  },
  display: { fontSize: 'clamp(2rem, 4vw, 3.25rem)', fontWeight: 650, lineHeight: 1.1, letterSpacing: '-0.02em' },
  headline: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.01em' },
  title: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35 },
  body: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.55 },
  label: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.3, letterSpacing: '0.01em' },
  micro: { fontSize: '0.6875rem', fontWeight: 500, lineHeight: 1.25, letterSpacing: '0.01em' },
  timer: { fontSize: 'clamp(1.75rem, 4vw, 3rem)', fontWeight: 500, lineHeight: 1, letterSpacing: '-0.01em' },
} as const;

export const radius = {
  sm: '6px',
  md: '10px',
  lg: '16px',
  full: '9999px',
} as const;

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '40px',
} as const;

export const shadow = {
  popover: '0 4px 16px oklch(0.20 0.01 90 / 0.12)',
  modal: '0 12px 40px oklch(0.20 0.01 90 / 0.18)',
  drag: '0 8px 24px oklch(0.20 0.01 90 / 0.16)',
} as const;
